import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Check, ExternalLink, X, Gift, Sparkles } from 'lucide-react';

export const GiftLinkModal = ({ open, onClose, giftId, expiresAt }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;
  const giftUrl = `${window.location.origin}/gift/${giftId}`;
  const days = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 30;

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(giftUrl);
      } else {
        // execCommand fallback for insecure contexts
        const ta = document.createElement('textarea');
        ta.value = giftUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error('Copy failed — long-press the link to copy manually.');
    }
  };

  return (
    <div
      data-testid="gift-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/50"
      onClick={onClose}
    >
      <div
        data-testid="gift-modal"
        className="relative w-full max-w-md bg-paper border-2 border-ink shadow-brutXl rounded-sm grain"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="gift-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 neo-btn bg-paper h-9 w-9 z-10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-7">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 border-2 border-ink bg-sun shadow-brut flex items-center justify-center rotate-[-5deg]">
              <Gift className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <div>
              <span className="label-tiny block">Saved & shareable</span>
              <h2 className="font-outfit font-black text-2xl leading-tight">Gift link is live!</h2>
            </div>
          </div>

          <p className="font-work text-sm text-ink/75 leading-relaxed mb-5">
            Anyone with this link can preview your collage. They can pay $1.99 to download the full HD file too — every share keeps the loop going.
          </p>

          {/* URL copy field */}
          <div className="border-2 border-ink rounded-sm bg-canvasbg p-2 flex items-center gap-2 mb-3">
            <input
              data-testid="gift-link-input"
              readOnly
              value={giftUrl}
              className="flex-1 bg-transparent border-0 px-2 py-2 font-mono text-xs text-ink outline-none truncate"
              onFocus={(e) => e.target.select()}
            />
            <button
              data-testid="gift-link-copy"
              onClick={copyToClipboard}
              className="neo-btn bg-mint h-9 px-3 text-xs"
            >
              {copied ? (
                <><Check className="h-3.5 w-3.5 mr-1" /> Copied</>
              ) : (
                <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>
              )}
            </button>
          </div>

          <a
            data-testid="gift-link-open"
            href={giftUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="neo-btn bg-lilac w-full h-11 text-sm mb-4 inline-flex"
          >
            <ExternalLink className="h-4 w-4 mr-2" /> Preview the gift page
          </a>

          <div className="flex items-center gap-2 text-xs text-ink/60">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-work">
              Expires in <strong className="font-outfit text-ink">{days} day{days === 1 ? '' : 's'}</strong> · revoke any time by deleting your account
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
